import React, { memo, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CallForwardingSettingsContent from './call-forwarding-settings-content';
import { RootState } from '../../../../../../../../../../../store/root-reducer';
import { PhoneNumberSettingCode } from '../../../../../../../../../types/phone-numbers';
import {
  updatePhoneNumberSettingsRequest,
  getPhoneNumberDetailsRequest,
} from '../../../../../../../../../extra-actions';
import { RequestStatus } from '../../../../../../../../../../../shared/enums/request-status';
import {
  resetGetPhoneNumberDetailsRequest,
  resetUpdatePhoneNumberSettingsRequest,
} from '../../../../../../../../../dialer-slice';
import toaster, {
  Theme,
} from '../../../../../../../../../../../shared/toaster';

const DEFAULT_MODAL_SETTINGS_MAP: Record<PhoneNumberSettingCode, string | null> = {
  [PhoneNumberSettingCode.FORWARDING_ENABLED]: 'true',
  [PhoneNumberSettingCode.FORWARDING_ALWAYS_ENABLED]: 'true',
  [PhoneNumberSettingCode.FORWARDING_ALWAYS_NUMBER]: null,
  [PhoneNumberSettingCode.FORWARDING_UNANSWERED_ENABLED]: 'false',
  [PhoneNumberSettingCode.FORWARDING_UNANSWERED_SECONDS]: '20',
  [PhoneNumberSettingCode.FORWARDING_UNANSWERED_NUMBER]: null,
  [PhoneNumberSettingCode.FORWARDING_BUSY_ENABLED]: 'false',
  [PhoneNumberSettingCode.FORWARDING_BUSY_NUMBER]: null,
  [PhoneNumberSettingCode.FORWARDING_OOF_ENABLED]: 'false',
  [PhoneNumberSettingCode.FORWARDING_OOF_NUMBER]: null,
  [PhoneNumberSettingCode.FORWARDING_OOF_START]: null,
  [PhoneNumberSettingCode.FORWARDING_OOF_END]: null,
  [PhoneNumberSettingCode.VOICEMAIL_URL]: null,
  [PhoneNumberSettingCode.VOICEMAIL_ENABLED]: null,
  [PhoneNumberSettingCode.CALL_RECORDING_ENABLED]: null,
  [PhoneNumberSettingCode.TRANSCRIPTION_LANGUAGE]: null,
};

const CallForwardingSettingsContentContainer: React.FC<{
  isModal?: boolean;
  getIsForwardingConfigurationValid?: (isValid: boolean) => void;
  setIsForwardingEnabled?: (isForwardingEnabled: boolean) => void;
  setSettingsToApply?: (
    settings: Array<{ code: string; value: string | null }>,
  ) => void;
}> = ({
  isModal = false,
  getIsForwardingConfigurationValid,
  setIsForwardingEnabled,
  setSettingsToApply,
}) => {
  const dispatch = useDispatch();
  const activeNumberSettings = useSelector(
    (state: RootState) => state.dialer.activeNumberSettings,
  );
  const updatePhoneNumberSettingsRequestStatus = useSelector(
    (state: RootState) =>
      state.dialer.updatePhoneNumberSettingsRequest[
        PhoneNumberSettingCode.FORWARDING_ENABLED
      ]?.status || RequestStatus.Ideal,
  );

  // Create a map for easy lookup of setting values by code
  const settingsMap: Record<
    PhoneNumberSettingCode,
    string | null
  > = useMemo(() => {
    if (!activeNumberSettings?.settings) {
      return {} as Record<PhoneNumberSettingCode, string | null>;
    }

    return activeNumberSettings.settings.reduce((acc, setting) => {
      acc[setting.code as PhoneNumberSettingCode] = setting.value;
      return acc;
    }, {} as Record<PhoneNumberSettingCode, string | null>);
  }, [activeNumberSettings]);

  const handleSaveForwardingConfiguration = useCallback(
    (settings: Array<{ code: string; value: string | null }>) => {
      if (!activeNumberSettings?.id) {
        return;
      }

      dispatch(
        updatePhoneNumberSettingsRequest({
          phoneNumberIds: [activeNumberSettings.id],
          settings,
        }),
      );
    },
    [dispatch, activeNumberSettings?.id],
  );

  const handleResetGetPhoneNumberDetailsRequest = useCallback(() => {
    dispatch(resetGetPhoneNumberDetailsRequest());
  }, [dispatch]);

  const getPhoneNumberDetailsRequestStatus = useSelector(
    (state: RootState) => state?.dialer?.getPhoneNumberDetailsRequest?.status,
  );

  // Refresh phone number details on successful save
  useEffect(() => {
    if (
      !isModal &&
      updatePhoneNumberSettingsRequestStatus === RequestStatus.Succeeded &&
      activeNumberSettings?.id
    ) {
      dispatch(getPhoneNumberDetailsRequest(activeNumberSettings.id));
      dispatch(
        resetUpdatePhoneNumberSettingsRequest(
          PhoneNumberSettingCode.FORWARDING_ENABLED,
        ),
      );
      toaster.success('Number Setting Updated Successfully', {
        theme: Theme.New,
      });
    }
  }, [
    updatePhoneNumberSettingsRequestStatus,
    activeNumberSettings?.id,
    dispatch,
    isModal,
  ]);

  return (
    <CallForwardingSettingsContent
      isModal={isModal}
      settingsMap={isModal ? DEFAULT_MODAL_SETTINGS_MAP : settingsMap}
      activeNumberId={isModal ? undefined : activeNumberSettings?.id}
      updatePhoneNumberSettingsRequestStatus={
        updatePhoneNumberSettingsRequestStatus
      }
      onSaveForwardingConfiguration={handleSaveForwardingConfiguration}
      passIsForwardingConfigurationValid={getIsForwardingConfigurationValid}
      setIsForwardingEnabledForModal={setIsForwardingEnabled}
      setSettingsToApplyForModal={setSettingsToApply}
      activeNumberSettings={isModal ? null : activeNumberSettings}
      resetGetPhoneNumberDetailsRequest={
        handleResetGetPhoneNumberDetailsRequest
      }
      getPhoneNumberDetailsRequestStatus={getPhoneNumberDetailsRequestStatus}
    />
  );
};

export default memo(CallForwardingSettingsContentContainer);
